import { createApp, nextTick } from "vue";
import store from "./store";
import vSelect from "vue-select"; // https://vue-select.org
import ConfirmDialog from "vue3-confirm-dialog";
import "vue3-confirm-dialog/style";
import Toast from "vue-toastification"; // https://vue-toastification.maronato.dev/
import "vue-toastification/dist/index.css";
// v-calendar CSS removed - using @vuepic/vue-datepicker instead
import App from "./App.vue";
import router from "./router";
import Switcher from "./components/Switcher.vue";
import LoadingSpinner from "./components/LoadingSpinner.vue";
import ApiService from "./services/api.service";
import { TokenService } from "./services/storage.service";
import PosthogPlugin from "./plugins/posthog";
import general from "./utils/general";
import eventBus from "./services/eventBus";
import { confirm, alert } from "./services/confirmDialog.service";

// Vue 3 native mode - compatibility layer removed
const apiBaseURL =
	process.env.VUE_APP_API_BASE_URL ||
	(process.env.NODE_ENV === "development" ? "/api/" : "https://" + window.location.hostname + "/api/");
import { library } from "@fortawesome/fontawesome-svg-core";
import {
	faSignOutAlt,
	faExchangeAlt,
	faProjectDiagram,
	faHandshake,
	faPaperPlane,
	faAt,
	faXmark,
	faEnvelope,
	faLeaf,
	faSquareCheck,
	faEnvelopeOpenText,
	faIcons,
	faPuzzlePiece,
	faRectangleXmark,
	faMapPin,
	faFire,
	faStar,
	faHouse,
	faPerson,
	faLightbulb,
	faRightFromBracket,
	faRightToBracket,
	faCircleCheck,
	faCircleXmark,
	faUserPlus,
	faBullhorn,
	faMedal,
	faHeart,
	faComments,
	faCoins,
	faGift,
	faHandHoldingHeart,
	faMagnifyingGlassPlus,
	faPeopleGroup,
	faBookmark,
	faCartShopping,
	faCalendar,
	faDollarSign,
	faGlobe,
	faLeftLong,
	faRightLong,
	faCircleQuestion,
	faBan,
	faBell,
	faArrowUpFromBracket,
	faSeedling,
	faCircleUser,
	faIdCard,
	faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { faXTwitter, faFacebookF, faTiktok, faInstagram, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Set the base URL of the API
ApiService.init(apiBaseURL);

// Suppress harmless ResizeObserver errors from Flicking carousel
const resizeObserverErrDiv = "ResizeObserver loop completed with undelivered notifications.";
const resizeObserverErr = "ResizeObserver loop limit exceeded";

window.addEventListener("error", (e) => {
	if (e.message && (e.message.includes("ResizeObserver loop") || e.message === resizeObserverErrDiv || e.message.includes(resizeObserverErr))) {
		e.stopImmediatePropagation();
		e.preventDefault();
		return false;
	}
});

// Also try to suppress unhandled promise rejections related to ResizeObserver
window.addEventListener("unhandledrejection", (e) => {
	if (e.reason && typeof e.reason === "string" && e.reason.includes("ResizeObserver")) {
		e.preventDefault();
		return false;
	}
});

// If token exists set header
if (TokenService.getToken()) {
	ApiService.setHeader();
	ApiService.mountInterceptor();
}

library.add(
	faSignOutAlt,
	faExchangeAlt,
	faProjectDiagram,
	faHandshake,
	faPaperPlane,
	faXTwitter,
	faFacebookF,
	faTiktok,
	faAt,
	faInstagram,
	faXmark,
	faEnvelope,
	faLeaf,
	faSquareCheck,
	faEnvelopeOpenText,
	faIcons,
	faPuzzlePiece,
	faRectangleXmark,
	faMapPin,
	faFire,
	faStar,
	faHouse,
	faPerson,
	faLightbulb,
	faRightFromBracket,
	faRightToBracket,
	faLinkedin,
	faCircleCheck,
	faCircleXmark,
	faUserPlus,
	faBullhorn,
	faMedal,
	faHeart,
	faComments,
	faCoins,
	faGift,
	faHandHoldingHeart,
	faMagnifyingGlassPlus,
	faPeopleGroup,
	faBookmark,
	faCartShopping,
	faCalendar,
	faDollarSign,
	faGlobe,
	faLeftLong,
	faRightLong,
	faCircleQuestion,
	faBan,
	faBell,
	faArrowUpFromBracket,
	faSeedling,
	faCircleUser,
	faIdCard,
	faPhone
);

// Create Vue 3 app instance
const app = createApp(App);

const hasRenderableToastContent = (candidate) => {
	const content = candidate && typeof candidate === "object" && "content" in candidate ? candidate.content : candidate;

	if (typeof content === "string") return content.trim().length > 0;
	if (typeof content === "number") return true;
	if (Array.isArray(content)) return content.some((item) => hasRenderableToastContent(item));
	if (!content) return false;

	if (typeof content === "object") {
		if ("component" in content) {
			return hasRenderableToastContent(content.component);
		}
		if (typeof content.children === "string") {
			return content.children.trim().length > 0;
		}
		if (Array.isArray(content.children)) {
			return content.children.some((item) => hasRenderableToastContent(item));
		}
	}

	// Allow VNodes/components by default after known empty wrappers have been rejected.
	return true;
};

// Register global components
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("v-select", vSelect);
app.component("AppSwitcher", Switcher);
app.component("LoadingSpinner", LoadingSpinner);

// Use plugins
app.use(store);
app.use(router);
app.use(ConfirmDialog);

app.use(Toast, {
	timeout: 7500,
	showCloseButtonOnHover: true,
	closeOnClick: true,
	pauseOnHover: true,
	position: "top-right",
	allowHtml: true,
	transition: "Vue-Toastification__bounce",
	maxToasts: 5,
	newestOnTop: true,
	toastDefaults: {
		// Custom PartnerUp branded toast type
		partnerup: {
			timeout: 8000,
			closeButton: true,
			pauseOnFocusLoss: false,
		},
	},
	filterBeforeCreate: (toast) => {
		return hasRenderableToastContent(toast) ? toast : false;
	},
});

// Register directives
app.directive("scroll", {
	mounted: function (el, binding) {
		let f = function (evt) {
			if (binding.value(evt, el)) {
				window.removeEventListener("scroll", f);
			}
		};
		window.addEventListener("scroll", f);
	},
});

// Configure app
app.config.globalProperties.productionTip = false;

// Add event bus to global properties for Options API components
app.config.globalProperties.$eventBus = eventBus;

if (window.location.hostname === "brands.partneruphub.com" || window.location.hostname === "brands-dev.partneruphub.com") {
	app.use(PosthogPlugin);

	router.afterEach(() => {
		nextTick(() => {
			general.posthogCapture("$pageview");
		});
	});
}

// Provide services for Composition API injection
// Note: $toast is now accessed directly via useToast() composable
app.provide("$confirm", app.config.globalProperties.$confirm);
app.provide("$dialog", { confirm, alert });
app.provide("eventBus", eventBus);

// Mount the app
app.mount("#app");
